import React from 'react';
import { trackTermsAcceptance } from '../utils/gtmTracking';
import { PlanAcknowledgments } from './PlanAcknowledgments';

/**
 * Helper function to format currency with commas
 */
const formatCurrency = (amount, showDecimals = true) => {
  if (showDecimals) {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } else {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }
};

/**
 * Helper function to format discount display based on type
 */
const formatDiscountDisplay = (promo) => {
  if (promo.discountInfo?.type === 'trial') {
    return `${promo.discountInfo.amount} ${promo.discountInfo.unit} free`;
  } else if (promo.discountInfo?.type === 'percent') {
    return `${promo.discount}% off`;
  } else {
    // Fixed amount discount
    return `-${formatCurrency(promo.discount)}`;
  }
};

/**
 * Cost summary breakdown
 */
const CostSummary = ({
  baseCost,
  setupFee,
  appliedPromoCodes,
  totalDueToday,
  hasFreeTrial
}) => (
  <div className="cost-summary-box">
    <div className="cost-item">
      <span className='op-80'>Subscription</span>
      <span className="weight-600 tabular-num font-mono">${formatCurrency(baseCost)}</span>
    </div>
    <div className="cost-item">
      <span className='op-80'>One-time setup fee</span>
      <span className="weight-600 tabular-num font-mono">${formatCurrency(setupFee)}</span>
    </div>
    {appliedPromoCodes.map((promo, index) => (
      <div key={index} className="cost-item">
        <span className='op-80' style={{ color: '#4caf50' }}>{promo.description}</span>
        <span className="weight-600 tabular-num font-mono" style={{ color: '#4caf50' }}>
          {formatDiscountDisplay(promo)}
        </span>
      </div>
    ))}
    <div className="cost-total">
      <span>Total Due Today</span>
      <span className='cost-total-price tabular-num'>${hasFreeTrial ? 0 : formatCurrency(totalDueToday)}</span>
    </div>
    {hasFreeTrial && (
      <div style={{
        fontSize: '0.8rem',
        color: '#aaa',
        marginTop: '0.5rem',
        textAlign: 'center'
      }}>
        * Subscription billing will begin after your free trial period
      </div>
    )}
  </div>
);

/**
 * Terms acceptance checkbox
 */
const TermsCheckbox = ({
  termsAccepted,
  handleFieldChange,
  getFormContext,
  error
}) => (
  <div className="form-group checkbox-group flex flex-col mt-4">
    <label className="custom-checkbox checkbox-well ml-0">
      <input
        type="checkbox"
        id="terms"
        checked={termsAccepted}
        onChange={(e) => {
          handleFieldChange('termsAccepted', e.target.checked);
          trackTermsAcceptance(e.target.checked, getFormContext());
        }}
      />
      <span className="checkmark"></span>
      <span className="checkbox-label-text">
        I accept Homebot's <a href="https://homebot.ai/terms-of-service" target="_blank" rel="noreferrer" className="hover-underline">Terms of Service</a> and <a href="https://homebot.ai/privacy-policy" target="_blank" rel="noreferrer" className="hover-underline">Privacy Policy</a>
      </span>
    </label>
    {error && <p className="error mt-2">{error}</p>}
  </div>
);

/**
 * Submit button
 */
const SubmitButton = ({ isSubmitting }) => (
  <>
    <button
      type="submit"
      className="confirm-button"
      disabled={isSubmitting}
    >
      {isSubmitting ? 'Processing...' : 'Confirm & Subscribe'}
    </button>
    <div className="button-subtext mt-1">
      <p className="text-shadow-sm">
        &#128274; Payments are secure and encrypted
      </p>
    </div>
  </>
);

/**
 * Main ConfirmationSection component
 */
const ConfirmationSection = ({
  selectedPlanDetails,
  baseCost,
  appliedPromoCodes,
  totalDueToday,
  hasFreeTrial,
  termsAccepted,
  acknowledgments,
  onAcknowledgmentChange,
  handleFieldChange,
  getFormContext,
  errors,
  hasValidationErrors,
  isSubmitting
}) => (
  <div className="form-section-single">
    <h2 className="form-section-single-legend">Confirmation</h2>
    <p className="op-90 leading-body text-md">
      You're about to take advantage of a subscription to Homebot's <strong>{selectedPlanDetails?.planName || 'Plan'} plan for {selectedPlanDetails?.customerType || 'Customer'}s</strong>.
    </p>
    <p className="op-90 leading-body text-md">After this, you can add clients right away &#127881;</p>

    <CostSummary
      baseCost={baseCost}
      setupFee={selectedPlanDetails?.setupFee || 0}
      appliedPromoCodes={appliedPromoCodes}
      totalDueToday={totalDueToday}
      hasFreeTrial={hasFreeTrial}
    />

    <TermsCheckbox
      termsAccepted={termsAccepted}
      handleFieldChange={handleFieldChange}
      getFormContext={getFormContext}
      error={errors.termsAccepted}
    />

    {selectedPlanDetails?.acknowledgments?.length > 0 && (
      <PlanAcknowledgments
        acknowledgments={selectedPlanDetails.acknowledgments}
        acceptedAcknowledgments={acknowledgments}
        onAcknowledgmentChange={onAcknowledgmentChange}
        errors={errors}
      />
    )}

    <hr className="w-100" style={{ opacity: 0.08 }} />
    {errors.submit && <span className="error submit-error">{errors.submit}</span>}

    <p className="cancellation-policy text-center w-100 mx-auto">
      Cancel anytime in your billing settings <strong style={{ color: '#fff' }}>at least one day</strong> before the renewal date. Your plan automatically renews until cancelled.
    </p>

    {hasValidationErrors && (
      <p className="error submit-error text-center">
        Please fix the errors above before submitting.
      </p>
    )}

    <SubmitButton isSubmitting={isSubmitting} />
  </div>
);

export default ConfirmationSection;
